import { useCallback, useEffect, useState, useRef } from 'react';
import type { ValidationErrors } from '../../utils/validation';
import isEqual from 'lodash/isEqual';

export type FormProps<Values extends Record<string, unknown>> = {
  initialValues: Values;
  validate: (values: Values) => ValidationErrors<Values>;
  /**
   * Resets form values to initial values when initialValues change.
   * Always make sure that initialValues are memoized or referentially stable.
   */
  enableReinitialize?: boolean;
};

export const hasErrors = (errors: ValidationErrors<Record<string, unknown>>) =>
  Object.values(errors).some(Boolean);

const isDirty = (dirty: Record<string, boolean>) =>
  Object.values(dirty).some(Boolean);

export const useNarrativeForm = <Values extends Record<string, unknown>>({
  initialValues,
  validate,
  enableReinitialize,
}: FormProps<Values>) => {
  const [values, setValues] = useState(initialValues);
  const [errors, setErrors] = useState<ValidationErrors<Values>>({});
  const [submitCount, setSubmitCount] = useState(0);
  const initialValuesRef = useRef(initialValues);

  const validateForm = useCallback(() => {
    const errors = validate(values);
    setErrors(errors);
    return errors;
  }, [validate, values]);

  useEffect(() => {
    if (submitCount > 0) {
      validateForm();
    }
  }, [validateForm, submitCount]);

  useEffect(() => {
    if (enableReinitialize) {
      if (!isEqual(initialValuesRef.current, initialValues)) {
        initialValuesRef.current = initialValues;
        setValues(initialValues);
        setErrors({});
      }
    }
  }, [enableReinitialize, initialValues]);

  const setFieldValue = useCallback(
    (fieldName: keyof Values, value: unknown) => {
      setValues((currentValues) => {
        if (isEqual(value, initialValuesRef.current[fieldName])) {
          return currentValues;
        }
        return {
          ...currentValues,
          [fieldName]: value,
        };
      });
    },
    []
  );

  const setPartialValues = useCallback(
    (partialValues: Partial<Values>) =>
      setValues((currentValues) => ({ ...currentValues, ...partialValues })),
    []
  );

  const dirty = Object.fromEntries(
    Object.entries(values).map(([fieldName, value]) => {
      const isFieldDirty = !isEqual(value, initialValuesRef.current[fieldName]);
      return [fieldName, isFieldDirty];
    })
  ) as Record<keyof Values, boolean>;

  const resetForm = useCallback(() => {
    setValues(initialValuesRef.current);
    setErrors({});
  }, []);

  return {
    values,
    setValues,
    setPartialValues,
    setFieldValue,
    errors,
    dirty,
    isDirty: isDirty(dirty),
    submitCount,
    setSubmitCount,
    validateForm,
    resetForm,
    hasErrors: hasErrors(errors),
  };
};
